"use client";
import * as React from "react";
import { IPlace } from "@/interfaces/property";
import { useTranslation } from "@/lib/i18n";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";


interface PlaceDetailsCardProps {
  place: IPlace & {
    notes?: string | null;
    google_maps_url?: string;
    location: IPlace["location"] & {
      rating?: number;
      google_maps_url?: string;
      distance?: { value: number; metric?: string };
    };
  };
  onClose?: () => void;
}

const PlaceDetailsCard = React.forwardRef<HTMLElement, PlaceDetailsCardProps>(({ place, onClose }, ref) => {
  const { t } = useTranslation();
  const { location, notes, google_maps_url } = place;

  // Distance formatting
  const formatDistance = (distance: number, metric?: string) => {
    if (!metric || metric === "meters") {
      if (distance < 1000) {
        return `${Math.round(distance)}m`;
      } else {
        return `${(distance / 1000).toFixed(1)}km`;
      }
    } else if (metric === "km" || metric === "kilometers") {
      return `${distance}km`;
    }
    return `${distance}${metric}`;
  };

  // Rating formatting
  const renderRating = () => {
    if (location.rating && location.rating > 0) {
      return (
        <span className="flex items-center gap-1 font-medium text-sm md:text-md">
          <span>{location.rating.toFixed(1)}</span>
          <svg width="16" height="16" fill="currentColor" className="text-yellow-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z"/></svg>
        </span>
      );
    }
    return null;
  };

  // Distance text
  const distanceText = location.distance?.value !== undefined && location.distance?.value !== null
    ? formatDistance(location.distance.value, location.distance.metric)
    : null;

  // Action buttons
  const actions = [
    {
      label: t("places.get_directions") || "Directions",
      icon: "/assets/img/propertyButtonDirections.svg",
      onClick: () => {
        const url = google_maps_url || location.google_maps_url;
        if (url) {
          window.open(url, "_blank");
        }
      },
      show: !!(google_maps_url || location.google_maps_url),
      primary: true
    },
    {
      label: t("places.call") || "Call",
      icon: "/assets/img/propertyButtonContactHost.svg",
      onClick: () => {
        if (location.contact?.phone) {
          window.open(`tel:${location.contact.phone}`, '_blank').focus();
        }
      },
      show: !!location.contact?.phone,
      primary: false
    },
    {
      label: t("places.website") || "Website",
      icon: "/assets/img/buttonWebsiteLink.svg",
      onClick: () => {
        if (location.contact?.website) {
          window.open(location.contact.website, '_blank').focus();
        }
      },
      show: !!location.contact?.website,
      primary: false
    }
  ];

  return (
    <article id="PlaceDetailsCard" ref={ref} className="overflow-visible p-0 flex flex-col w-full bg-background mx-auto gap-[16px] md:gap-[24px] px-[8px] md:px-0">

      {/* Header */}
      <div id="PlaceHeader" 
          className="w-full p-0 flex items-start justify-between mt-[-8px] md:mt-[-5px]">
        {/* Title */}
        <h2 className="text-xl md:text-2xl font-bold text-left flex-1 mt-[8px] md:mt-[5px]">{location.name}</h2>

        {/* Close Button */}
        {onClose && (
          <button
          className="pointer-events-auto w-[40px] h-[40px] flex items-center justify-center"
          style={{ marginRight: "-10px" }}
          onClick={onClose}
          aria-label="Close">
            <img
              src="/assets/img/buttonClose.svg"
              alt="Close"
              className="w-[12px] h-[12px] object-contain"
            />
          </button>
        )}
      </div>

      {/* Ratings and distance */}
      <div id="RatingsAndDistance" className="flex items-center gap-1 text-sm md:text-md font-medium">
        {renderRating()}
        {location.rating && location.rating > 0 && distanceText && <span className="mx-1">·</span>}
        {distanceText && <span>{distanceText} {t('from_property')}</span>}
      </div>

      {/* Image Carousel
        DONE
        - gap-[10px] md:gap-[16px]
        - image
          - mobile 206 x 244
          - desktop 276 x 326
      */}
      {location.photos && location.photos.length > 0 && (
        <div id="ImageCarousel" className="w-full overflow-visible mr-[-100px]">
          <Swiper
            //modules={[Navigation, A11y]}
            //navigation
            spaceBetween={'10px'}
            slidesPerView={1.2}
            breakpoints={{
              480: {
                slidesPerView: 2.2,
                spaceBetween: '10px',
              },
              768: {
                slidesPerView: 1.2,
                spaceBetween: '16px',
              },
              1024: {
                slidesPerView: 'auto',
                spaceBetween: '16px',
              },
            }}
            lazyPreloadPrevNext={3}
            //resistance={false}
            //resistanceRatio={276/326}
            //resizeObserver={true}
            //updateOnWindowResize={true}
            //className="overflow-visible w-full"
            className="overflow-x-auto w-full no-scrollbar"
            //style={{ maxHeight: 276 }}
            //style={{ overflow: 'visible'; width: '100%' }}
            
          >
            {location.photos.map((photo, idx) => (
              <SwiperSlide 
                key={photo.url || idx}
                className="w-[206px] md:w-[276px] aspect-[276/326] h-auto"
              >
                <img
                  src={photo.url}
                  alt={location.name}
                  className="object-cover overflow-hidden rounded-2xl w-full aspect-[276/326] h-auto"
                  loading={idx === 0 ? "eager" : "lazy"}
                />

                {/* Google logo - show when provider is Google */}
                {place.location.provider?.key === "GOOGLE" && (
                  <img
                    src="/assets/img/logoGoogle.svg"
                    alt="Google logo"
                    className="absolute bottom-2 right-2 object-contain shrink-0 w-[28px] md:w-[32px] aspect-square"
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* About Section */}
      { notes && (
        <section id="About" className="flex flex-col gap-[8px] w-full">
          <h2 className="font-semibold text-sm md:text-md">{t(`places.about`)}:</h2>
          <div className="bg-secondary text-foreground rounded-lg p-[12px] text-sm md:text-md">
            {notes}
          </div>
        </section>
      )
      }

      {/* Action Buttons 
        DONE
        - Radius 10px
        - Gap: 10
        - Icon: 16
        - height: 54 (width: 144 desk ; 107)
        - Gap icon and text: 5px
        - Font: 13px desk ; 11
      */}
      <div id="Buttons" className="flex flex-row gap-[10px] w-full">
        {actions.filter(a => a.show).map((action, idx, arr) => (
          <button
            key={action.label}
            onClick={action.onClick}
            className={`flex flex-col items-center justify-center gap-[10px] h-[54px] rounded-xl font-semibold transition-colors max-w-[144px] sm:max-w-[233px]
              ${action.primary ? "bg-accent text-accent-foreground" : "bg-secondary text-foreground"}
              ${arr.length === 1 ? "flex-1" : arr.length === 2 ? "w-1/2" : "w-1/3"}`}
            style={{ minWidth: 0 }}
          >
            <img
              src={action.icon}
              className={`w-[16px] h-auto ${action.primary ? "filter brightness-0 invert" : ""}`}
              style={action.primary ? { filter: "brightness(0) invert(1)" } : {}}
            />
            <span className="text-center text-sm font-semibold leading-none">{action.label}</span>
          </button>
        ))}
      </div>
    </article>
  );
});

PlaceDetailsCard.displayName = "PlaceDetailsCard";

export { PlaceDetailsCard };  